import { concat, cloneDeep, isEmpty, get } from 'lodash';
import type from './costingTemplate.types';
import type { CostingTemplateAction, CostingTemplateDetail, CostingTemplateListPayload, CostingTemplateState } from './costingTemplate.types';

/*
 * initialState is a byte-for-byte copy of the JavaScript original
 * (Library/CostingTemplate/store/CostingTemplate.reducer.js:4-19). The slice is still mounted under
 * the key `CostingTemplateData`, which roughly 23 sites outside this module read directly, so the
 * shape must not drift.
 */
const initialState: CostingTemplateState = {
  costingModalState: false,
  costingTemplateList: [],
  costingTemplateListCount: 0,
  costingTemplateDetail: null,
  additionalModalState: false,
  landedInfoModalState: false,
  searchCostingColumnValue: '',
  updateAdditionalInfoData: null,
  updateLandedInfoData: null,
  customColumnList: [],
  customList: [],
  selectedIds: { additonalIds: [], landedIds: [] },
  additionalRow: [],
  landedRow: [],
};

/**
 * Decides whether a list payload replaces the current page or appends to it.
 *
 * A search result always replaces unless it is itself a scroll of that search; a plain scroll
 * appends; anything else replaces. Ported unchanged — the list page's infinite scroll and its
 * debounced search both key off this.
 * @param array the list currently held in state.
 * @param payload the incoming page.
 * @returns the list to store.
 */
const setArrayByCase = (array: CostingTemplateDetail[], payload: CostingTemplateListPayload): CostingTemplateDetail[] => {
  if (!isEmpty(get(payload, 'searchText')) && get(payload, 'isScrolled')) {
    return [...array, ...payload.data];
  }
  if (!isEmpty(get(payload, 'searchText'))) {
    return payload.data;
  }
  if (get(payload, 'isScrolled')) {
    return concat(array, payload.data);
  }

  return payload.data;
};

const CostingTemplateReducer = (state: CostingTemplateState = initialState, action: CostingTemplateAction): CostingTemplateState => {
  switch (action.type) {
    case type.ADD_COSTING_MODAL_CONTROL:
      return {
        ...state,
        costingModalState: action.payload,
      };
    case type.SET_COSTING_TEMPLATE_LIST: {
      const updatedArry: CostingTemplateDetail[] = cloneDeep(setArrayByCase(state.costingTemplateList, action.payload));
      return {
        ...state,
        costingTemplateList: updatedArry,
        costingTemplateListCount: action.payload.totalCount as number,
      };
    }
    case type.UPDATE_NEW_CREATED_COSTING_TEMPLATE_LIST:
      return {
        ...state,
        costingTemplateList: concat(state.costingTemplateList, action.payload.data),
      };
    case type.SET_COSTING_TEMPLATE_DETAIL_BY_ID:
    case type.UPDATE_COLUMNDATA:
      return {
        ...state,
        costingTemplateDetail: action.payload,
      };

    case type.ADDITIONAL_MODAL:
      return {
        ...state,
        additionalModalState: action.payload,
        landedInfoModalState: false,
      };
    case type.LANDEDINFO_MODAL:
      return {
        ...state,
        landedInfoModalState: action.payload,
        additionalModalState: false,
      };
    case type.COSTING_TEMPLATE_UNMOUNT:
      return {
        ...state,
        ...initialState,
      };
    case type.REMOVE_COSTINGTEMPLATE:
      return {
        ...state,
        costingTemplateList: state.costingTemplateList.filter((val) => val._id !== action.payload),
      };

    case type.SEARCH_COLUMN:
      return {
        ...state,
        searchCostingColumnValue: action.payload,
      };

    case type.REMOVE_COSTINGTEMPLATE_COLUMN: {
      /*
       * Mutates `costingTemplateDetail` in place and returns the same reference, exactly as the
       * JavaScript original does (CostingTemplate.reducer.js:77-84). Cloning here would be the
       * correct reducer, but it changes which consumers re-render and when — that is a behaviour
       * change, so it belongs in its own ticket rather than in a conversion.
       */
      const updatedArray = state.costingTemplateDetail as CostingTemplateDetail;
      updatedArray[action.payload.type] = (state.costingTemplateDetail?.[action.payload.type] as CostingTemplateDetail[])?.filter(
        (val) => val._id !== action.payload.columnId,
      );
      return {
        ...state,
        costingTemplateDetail: updatedArray,
      };
    }
    case type.EDIT_ADDITIONAL_DATA:
      return {
        ...state,
        updateAdditionalInfoData: action.payload,
        additionalModalState: !!action.payload,
      };

    case type.EDIT_LANDED_DATA:
      return {
        ...state,
        updateLandedInfoData: action.payload,
        landedInfoModalState: !!action.payload,
      };
    case type.COPY_DATA:
      return {
        ...state,
        costingTemplateList: [...state.costingTemplateList, action.payload],
      };
    case type.SET_COSTING_CUSTOM_SECTION_LIST:
      return {
        ...state,
        customList: action.payload,
      };
    case type.SET_COSTING_CUSTOM_COLUMN_LIST:
      return {
        ...state,
        customColumnList: action.payload,
      };
    case type.SET_IDS_ADDITIONAL_AND_LANDED:
      return {
        ...state,
        selectedIds: action.payload,
      };
    case type.ADDITIONAL_ROW:
      return {
        ...state,
        additionalRow: [...state.additionalRow, action.payload],
      };
    case type.DELETE_UNSAVED_ADDITIONAL_ROW: {
      const cloneAddtionalRow = [...(state?.additionalRow ?? [])];
      cloneAddtionalRow.splice(action.payload, 1);
      return {
        ...state,
        additionalRow: cloneAddtionalRow,
      };
    }
    case type.LANDED_ROW:
      return {
        ...state,
        landedRow: [...state.landedRow, action.payload],
      };
    /* Braces added: the original declares `const` in a brace-less case, which TypeScript rejects.
       Syntax only — the behaviour is unchanged. */
    case type.DELETE_UNSAVED_LANDED_ROW: {
      const cloneLandedRow = [...(state?.landedRow ?? [])];

      cloneLandedRow.splice(action.payload, 1);
      return {
        ...state,
        landedRow: cloneLandedRow,
      };
    }
    default:
      return state;
  }
};

export default CostingTemplateReducer;
